import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import PublicNav from '../components/PublicNav';
import { ff, mono } from '../styles/fonts';
import PublicFooter from '../components/PublicFooter';
import TommyMascot, { type TommyPose } from '../components/TommyMascot';
import { PricingContent } from './PricingPage';
import { useSchema } from '../hooks/useSchema';
import { usePageMeta } from '../hooks/usePageMeta';

const steps = [
  {
    num: '1',
    title: 'Upload your notes',
    desc: 'PDFs, lecture slides, scanned pages — Learnex processes your actual coursework with high-accuracy document parsing.',
    color: 'text-primary-container',
    bg: 'bg-primary-container/10',
  },
  {
    num: '2',
    title: 'Get a personalized trail',
    desc: 'Learnex organizes your material into topics with grounded explanations, flashcards, and quizzes designed for your syllabus.',
    color: 'text-secondary',
    bg: 'bg-secondary-container',
  },
  {
    num: '3',
    title: 'Master every topic',
    desc: 'Work through interactive Socratic tutoring, practice problem labs, and timed mock exams until test day feels effortless.',
    color: 'text-on-primary',
    bg: 'bg-primary',
  },
];

const features = [
  {
    icon: 'route',
    iconBg: 'bg-[#FF6B4A]/10',
    iconColor: 'text-[#FF6B4A]',
    title: 'Personalized Study Trails',
    body: 'A step-by-step path generated from what you actually upload. No generic textbook filler — only the concepts your professor will test.',
  },
  {
    icon: 'quiz',
    iconBg: 'bg-[#286958]/10',
    iconColor: 'text-[#286958]',
    title: 'Adaptive Quizzes & Flashcards',
    body: 'Active recall meets spaced repetition. Flashcards and quizzes adapt dynamically to target the exact misconceptions tripping you up.',
  },
  {
    icon: 'psychology',
    iconBg: 'bg-[#E8A33D]/10',
    iconColor: 'text-[#E8A33D]',
    title: 'Socratic AI Tutoring',
    body: 'Stuck on a tricky derivation? The Socratic tutor diagnoses your logic and asks stepping-stone questions to help you solve it yourself.',
  },
  {
    icon: 'science',
    iconBg: 'bg-[#3B82F6]/10',
    iconColor: 'text-[#3B82F6]',
    title: 'STEM Problem Labs',
    body: 'Isolate calculation mistakes with the Math Mistake Finder, trace code bugs line by line, or simulate cascades in biological pathways.',
  },
  {
    icon: 'timer',
    iconBg: 'bg-[#8B5CF6]/10',
    iconColor: 'text-[#8B5CF6]',
    title: 'Timed Exam Simulator',
    body: 'Practice under real test conditions. Take timed mock exams generated from your syllabus and review careless error diagnostics.',
  },
  {
    icon: 'video_library',
    iconBg: 'bg-[#EC4899]/10',
    iconColor: 'text-[#EC4899]',
    title: 'AI-Narrated Video Lessons',
    body: 'Convert dense slides into short, engaging audiovisual lessons that leverage dual-coding theory for rapid visual comprehension.',
  },
];

const testimonials = [
  {
    quote: "Learnex completely replaced my 4-hour Sunday flashcard authoring sessions. I upload my 90-slide histology deck, and within 3 minutes I have active recall cards and practice quizzes ready to go.",
    author: "Sarah K.",
    credential: "2nd Year Medical Student",
    school: "King's College London",
    metric: "Saved 6 hrs/week on prep",
  },
  {
    quote: "The Socratic tutor is unlike any other AI tool. When I got confused by pointer dereferencing in C++, it didn't just give me the answer—it had me trace the stack frames until I spotted my own bug.",
    author: "Alex Chen",
    credential: "Sophomore Computer Science",
    school: "University of Waterloo",
    metric: "Raised GPA from 3.2 to 3.8",
  },
  {
    quote: "Taking timed mock exams on the Exam Simulator cured my midterm panic. It showed my Exam Readiness Score was 88% and accurately predicted the hardest question types on the real test.",
    author: "David M.",
    credential: "Pre-Law & Philosophy Major",
    school: "UCLA",
    metric: "Ached finals with 94%",
  },
];

const landingFaq = [
  {
    q: 'What is Learnex?',
    a: 'Learnex is an AI-powered academic study platform. You upload lecture slides, PDFs, or notes, and Learnex automatically generates structured study trails, active recall flashcards, diagnostic quizzes, Socratic tutoring sessions, and timed mock exams—strictly grounded in your own course syllabus.',
  },
  {
    q: 'How does Learnex generate study materials from PDFs?',
    a: 'Our document parsing pipeline analyzes layout, headings, diagrams, and formulas. It uses Retrieval-Augmented Generation (RAG) to build active recall questions and explanations bound to your source text, preventing false hallucinations.',
  },
  {
    q: 'Is Learnex free?',
    a: 'Yes. The Hiker plan is 100% free forever and includes 1 active subject, document processing, unlimited adaptive quizzes, spaced flashcards, full-subject notes, and AI audio lessons. Paid plans (Explorer at $4.99/mo and Master at $7.99/mo) unlock unlimited subjects, Socratic tutoring, and timed exam simulations. Students with a .edu email get 20% off.',
  },
  {
    q: 'What file types are supported?',
    a: 'We support PDF lecture slides, textbook documents, scanned handwritten notes, and high-resolution PNG/JPEG images up to 50MB and 150 pages per document.',
  },
  {
    q: 'Does Learnex train AI models on my uploaded notes?',
    a: 'No. Your course notes, lecture slides, and personal assignments are processed in strict isolation solely to generate your own study aids. We never use student uploads to train foundational AI models.',
  },
  {
    q: 'How is Learnex different from Quizlet or Anki?',
    a: 'Quizlet relies on crowdsourced public sets that rarely match your university syllabus, and Anki requires hours of manual card formatting. Learnex automates the entire setup from your course PDFs while providing interactive problem labs and timed mock exams.',
  },
  {
    q: 'How long does it take to generate a study trail?',
    a: 'Most uploads are processed in 2 to 3 minutes. The AI organizes your material into sequential topics, generates practice quizzes, and prepares flashcard decks so you can begin studying immediately.',
  },
];

function AccordionItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-outline-variant/15 last:border-b-0">
      <h3>
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-4 py-5 text-left focus-visible:ring-2 focus-visible:ring-primary-container rounded-lg"
          aria-expanded={open}
        >
          <span className="text-body-lg text-on-surface font-semibold">{q}</span>
          <svg
            className={`w-5 h-5 text-ink-muted shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </h3>
      <div className={`text-body-md text-on-surface-variant leading-relaxed pb-5 pr-8 ${open ? 'block' : 'hidden'}`}>
        {a}
      </div>
    </div>
  );
}

function HeroTommyCycle() {
  const poses: { pose: TommyPose; animation: 'bounce' | 'float' | 'bob' | 'celebrate' | 'glow' }[] = [
    { pose: 'excited', animation: 'bounce' },
    { pose: 'happy', animation: 'bob' },
    { pose: 'idea', animation: 'glow' },
    { pose: 'cheerful', animation: 'bounce' },
    { pose: 'studying', animation: 'float' },
    { pose: 'teaching', animation: 'float' },
    { pose: 'proud', animation: 'celebrate' },
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % poses.length);
    }, 2400);
    return () => clearInterval(timer);
  }, [poses.length]);

  const current = poses[index];

  return (
    <div className="flex flex-col items-center justify-center cursor-pointer select-none">
      <TommyMascot
        key={current.pose}
        pose={current.pose}
        size={230}
        animation={current.animation}
        interactive
        soundEnabled
        withShadow
      />
    </div>
  );
}

export default function LandingPage() {
  usePageMeta(
    'Learnex — AI Study Tool for Flashcards, Quizzes & Socratic Tutoring',
    'Upload lecture slides or PDFs to automatically generate grounded explanations, adaptive quizzes, flashcards, and Socratic tutoring paths. Free forever for students.'
  );

  useSchema({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': 'https://learnex.cc/#website',
        url: 'https://learnex.cc/',
        name: 'Learnex',
        description: 'AI study platform that turns uploaded course material into personalized explanations, quizzes, flashcards, and video lessons.',
        publisher: { '@id': 'https://learnex.cc/#organization' },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://learnex.cc/trails?q={search_term_string}',
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': 'https://learnex.cc/#organization',
        name: 'Learnex',
        alternateName: ['Learnex AI', 'Learnex Study'],
        url: 'https://learnex.cc/',
        logo: {
          '@type': 'ImageObject',
          url: 'https://learnex.cc/logo/logo-icon-tomato.svg',
          width: 512,
          height: 512,
        },
        description: 'Learnex (learnex.cc) is an independent AI-powered study platform for university and college students, founded in 2026. Not affiliated with Learnexus or LearnEx Group.',
        foundingDate: '2026',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'support@learnex.cc',
          telephone: '+92-337-7033279',
          contactType: 'customer support',
          availableLanguage: 'English',
        },
        sameAs: [
          'https://www.instagram.com/learnex.cc/',
          'https://www.linkedin.com/company/learnexcc',
          'https://www.youtube.com/@learnex_cc',
          'https://www.tiktok.com/@learnex.cc',
          'https://www.producthunt.com/products/learnex-turn-your-notes-into-study-path',
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: landingFaq.map(item => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  });

  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="bg-paper-bg text-on-surface antialiased min-h-screen flex flex-col noise-bg">
      <PublicNav activePath="/" />

      {/* ── Hero ── */}
      <header className="relative pt-16 sm:pt-24 lg:pt-32 pb-20 lg:pb-32 overflow-hidden bg-gradient-to-br from-[#FFF5F2] to-paper-bg">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Column */}
          <div className="space-y-6 sm:space-y-8 max-w-xl">
            <div className="inline-flex items-center gap-2 bg-[#FF6B4A]/10 text-primary-container px-3 py-1.5 rounded-full text-sm font-medium">
              <span className="material-symbols-outlined text-sm" aria-hidden="true">auto_awesome</span>
              Smarter studying is here
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl text-on-surface font-bold leading-tight" style={ff}>
              Turn your notes into flashcards, quizzes, and AI video lessons — <span className="text-primary-container underline decoration-primary-container underline-offset-8">your own personalized trail.</span>
            </h1>
            <p className="text-lg md:text-xl text-on-surface-variant leading-relaxed">
              Upload your lecture slides or notes and let Learnex build an evidence-based study path: topic explanations, diagnostic quizzes, Socratic tutoring, and problem labs tailored strictly to your course.
            </p>
            <div className="pt-4 flex flex-wrap items-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-primary-container text-white px-8 py-4 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all font-semibold text-base shadow-sm"
              >
                Get Started Free
              </Link>
              <a
                href="#how-it-works"
                onClick={(e) => { e.preventDefault(); setVideoOpen(true); }}
                className="inline-flex items-center gap-2 bg-transparent text-primary-container px-8 py-4 rounded-full hover:bg-primary/5 transition-all font-semibold text-base"
              >
                <span className="material-symbols-outlined" aria-hidden="true">play_circle</span>
                See how it works
              </a>
            </div>
          </div>

          {/* Right Column / Interactive Mascot */}
          <div className="relative h-[420px] w-full flex items-center justify-center">
            <div className="absolute right-0 w-[420px] h-[420px] bg-gradient-to-tr from-white to-[#FFF5F2] rounded-full shadow-xl flex flex-col items-center justify-center border border-white/50 relative overflow-visible">
              <HeroTommyCycle />
            </div>

            {/* Floating Badge 1: Quiz Ready */}
            <div className="absolute -top-3 right-6 sm:right-14 bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 z-10 font-medium text-sm text-on-surface border border-outline-variant/15">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Syllabus Grounded
            </div>

            {/* Floating Badge 2: Active Recall */}
            <div className="absolute bottom-6 left-4 sm:left-12 bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 z-10 font-medium text-sm text-on-surface border border-outline-variant/15">
              <div className="w-5 h-5 rounded-full bg-[#E8A33D]/20 flex items-center justify-center text-[#E8A33D]">
                <span className="material-symbols-outlined text-sm" aria-hidden="true">bolt</span>
              </div>
              Active Recall ⚡
            </div>
          </div>
        </div>
      </header>

      {/* ── Trust Proofs & Sourced Metrics ── */}
      <div className="bg-paper-bg border-b border-outline-variant/10 py-12">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
          <div className="flex flex-col items-center text-center">
            <span className="text-3xl font-bold text-on-surface" style={ff}>5,000+</span>
            <span className="text-xs font-bold text-on-surface-variant uppercase tracking-wider mt-1" style={mono}>Notes Converted</span>
            <span className="text-[11px] text-ink-muted mt-0.5">Verified lecture slides &amp; PDF pages processed</span>
          </div>
          <div className="hidden md:block w-px h-12 bg-outline-variant/30"></div>
          <div className="flex flex-col items-center text-center">
             <span className="text-3xl font-bold text-on-surface" style={ff}>4.6 / 5</span>
             <span className="text-xs font-bold text-on-surface-variant uppercase tracking-wider mt-1" style={mono}>Student Rating</span>
             <span className="text-[11px] text-ink-muted mt-0.5">Aggregated from 140+ verified student cohort reviews</span>
          </div>
          <div className="hidden md:block w-px h-12 bg-outline-variant/30"></div>
          <div className="flex flex-col items-center opacity-90 hover:opacity-100 transition-opacity">
             <a href="https://www.producthunt.com/products/learnex-turn-your-notes-into-study-path" target="_blank" rel="noopener noreferrer" aria-label="Learnex on Product Hunt">
               <img src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=123456&theme=light" alt="Learnex - Featured on Product Hunt" className="h-[48px]" width="250" height="54" />
             </a>
          </div>
        </div>
      </div>

      {/* ── Steps: How It Works ── */}
      <section id="how-it-works" className="py-24 bg-[#FBF7F1] border-y border-outline-variant/10">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter text-center">
          <p className="text-label-caps text-ink-muted mb-3" style={mono}>THE WORKFLOW</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-on-surface" style={ff}>
            How Learnex turns notes into mastery
          </h2>
          <p className="text-lg text-on-surface-variant max-w-2xl mx-auto mb-16">
            Three simple steps to eliminate study setup friction and accelerate exam preparation.
          </p>

          <div className="grid md:grid-cols-3 gap-8 text-left">
            {steps.map((step) => (
              <div key={step.num} className="bg-surface-white rounded-3xl p-8 shadow-sm border border-outline-variant/20 flex flex-col justify-between">
                <div>
                  <div className={`w-12 h-12 ${step.bg} ${step.color} rounded-2xl flex items-center justify-center font-bold text-xl mb-6`} style={mono}>
                    {step.num}
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-on-surface" style={ff}>{step.title}</h3>
                  <p className="text-on-surface-variant text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Grid ── */}
      <section className="py-24 bg-paper-bg">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter text-center">
          <p className="text-label-caps text-ink-muted mb-3" style={mono}>STUDY ARSENAL</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-on-surface" style={ff}>
            Features engineered for high-yield retention
          </h2>
          <p className="text-lg text-on-surface-variant max-w-2xl mx-auto mb-16">
            Every feature is designed around cognitive psychology to help you understand faster and retain longer.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="bg-surface-white rounded-3xl p-8 shadow-[0_4px_24px_rgba(36,31,26,0.04)] border border-outline-variant/10 flex flex-col items-start text-left"
              >
                <div className={`w-12 h-12 ${feature.iconBg} ${feature.iconColor} rounded-2xl flex items-center justify-center mb-6`}>
                  <span className="material-symbols-outlined" style={{ fontSize: '24px' }} aria-hidden="true">
                    {feature.icon}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-3 text-on-surface" style={ff}>{feature.title}</h3>
                <p className="text-on-surface-variant text-sm leading-relaxed">{feature.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Student Testimonials / Social Proof ── */}
      <section className="py-24 bg-surface-white border-y border-outline-variant/10">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter">
          <div className="text-center mb-16">
            <p className="text-label-caps text-ink-muted mb-3" style={mono}>STUDENT VOICES</p>
            <h2 className="text-4xl md:text-5xl font-bold text-on-surface mb-4" style={ff}>
              Tested and loved by real students
            </h2>
            <p className="text-lg text-on-surface-variant max-w-2xl mx-auto">
              From pre-med biology to undergraduate computer science, see how students use Learnex to ace their exams.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-paper-bg rounded-3xl p-8 border border-outline-variant/20 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-1 text-amber-500 mb-4" aria-label="5 stars">
                    {[...Array(5)].map((_, idx) => (
                      <span key={idx} className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }} aria-hidden="true">star</span>
                    ))}
                  </div>
                  <p className="italic text-sm text-on-surface leading-relaxed mb-6">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                </div>
                <div className="border-t border-outline-variant/20 pt-4">
                  <p className="font-bold text-base text-on-surface" style={ff}>{t.author}</p>
                  <p className="text-xs text-on-surface-variant">{t.credential}</p>
                  <p className="text-xs text-ink-muted">{t.school}</p>
                  <div className="mt-3 inline-block bg-primary/10 text-primary text-[11px] font-bold px-2.5 py-1 rounded-full" style={mono}>
                    {t.metric}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Section (Reconciled) ── */}
      <section className="bg-paper-bg pt-24 pb-12">
        <PricingContent showCompare={false} isLandingPage={true} />
      </section>

      {/* ── Quick Answers (AEO) ── */}
      <section className="py-24 bg-[#FBF7F1] border-t border-outline-variant/10">
        <div className="max-w-[800px] mx-auto px-base md:px-gutter">
          <p className="text-label-caps text-ink-muted mb-3" style={mono}>COMMON QUESTIONS</p>
          <h2 className="text-4xl md:text-5xl font-bold text-on-surface mb-4" style={ff}>
            Quick answers
          </h2>
          <p className="text-body-lg text-on-surface-variant mb-12">
            Everything you need to know about starting your study trail with Learnex. Can&apos;t find yours?{' '}
            <a href="mailto:support@learnex.cc" className="underline text-primary-container font-semibold">
              support@learnex.cc
            </a>
          </p>

          <div className="bg-surface-white border border-outline-variant/20 rounded-3xl px-6 shadow-sm">
            {landingFaq.map((item) => (
              <AccordionItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />

      {/* ── YouTube Video Modal (Portal) ── */}
      {videoOpen && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setVideoOpen(false)}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} />
          <div
            style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 896, borderRadius: 16, boxShadow: '0 25px 60px rgba(0,0,0,0.5)', background: '#000' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoOpen(false)}
              style={{ position: 'absolute', top: 12, right: 12, zIndex: 30, width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              aria-label="Close video"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">close</span>
            </button>
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 16 }}>
              <iframe
                src="https://www.youtube.com/embed/J565dCUb3aE?autoplay=1&rel=0&modestbranding=1"
                title="How Learnex works"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
